import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getAdminGenerationInstructions,
  getAdminSectionDefinitions,
  useCreateSectionDefinition,
  useDeleteSectionDefinition,
  useReorderSectionDefinitions,
  useUpdateGenerationInstructions,
  useUpdateSectionDefinition,
} from '../../queries/adminCompanyProfile';
import ConfirmDialog from '../ConfirmDialog';
import GlobalInstructionsPanel from './GlobalInstructionsPanel';
import GlobalInstructionsModal from './GlobalInstructionsModal';
import SectionDefinitionGrid from './SectionDefinitionGrid';
import SectionDefinitionFormModal from './SectionDefinitionFormModal';

const SCOPES = [
  { id: 'company', label: 'Company' },
  { id: 'program', label: 'Products' },
];

const emptyCardForm = {
  name: '',
  icon: 'building',
  generationInstructions: '',
  active: true,
  maxContentChars: 5000,
  role: 'standard',
};

const AdminCompanyProfileSetupPage = () => {
  const [scope, setScope] = useState('company');
  const [globalOpen, setGlobalOpen] = useState(false);
  const [globalDraft, setGlobalDraft] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formValues, setFormValues] = useState(emptyCardForm);
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: instructions, isLoading: isInstructionsLoading } = useQuery(
    getAdminGenerationInstructions(),
  );
  const { data: definitions = [], isLoading: isDefinitionsLoading } = useQuery(
    getAdminSectionDefinitions({ params: { scope } }),
  );

  const { mutateAsync: saveGlobal, isPending: isSavingGlobal } = useUpdateGenerationInstructions();
  const { mutateAsync: createCard, isPending: isCreating } = useCreateSectionDefinition();
  const { mutateAsync: updateCard, isPending: isUpdating } = useUpdateSectionDefinition();
  const { mutateAsync: reorderCards } = useReorderSectionDefinitions();
  const { mutateAsync: deleteCard, isPending: isDeleting } = useDeleteSectionDefinition();

  const sortedDefinitions = useMemo(
    () => [...definitions].sort((a, b) => a.order - b.order),
    [definitions],
  );

  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setFormValues(emptyCardForm);
    setFormOpen(true);
  };

  const openEdit = (definition) => {
    setFormMode('edit');
    setEditingId(definition._id);
    setFormValues({
      name: definition.name || '',
      icon: definition.icon || 'building',
      generationInstructions: definition.generationInstructions || '',
      active: definition.active !== false,
      maxContentChars: definition.maxContentChars || 5000,
      role: definition.role || 'standard',
    });
    setFormOpen(true);
  };

  const handleSaveCard = async () => {
    try {
      if (formMode === 'create') {
        await createCard({
          ...formValues,
          scope,
          maxContentChars: Number(formValues.maxContentChars),
        });
        toast.success('Card added');
      } else {
        await updateCard({
          id: editingId,
          data: { ...formValues, maxContentChars: Number(formValues.maxContentChars) },
        });
        toast.success('Card saved');
      }
      setFormOpen(false);
      setEditingId(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save card');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteCard({ id: pendingDelete._id });
      if (String(editingId) === String(pendingDelete._id)) {
        setFormOpen(false);
        setEditingId(null);
      }
      toast.success('Card deleted');
      setPendingDelete(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete card');
    }
  };

  const handleReorder = async (orderedIds) => {
    try {
      await reorderCards({ scope, orderedIds });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save order');
      throw error;
    }
  };

  const openGlobalEditor = () => {
    setGlobalDraft(instructions?.globalGenerationInstructions || '');
    setGlobalOpen(true);
  };

  const handleSaveGlobal = async () => {
    try {
      await saveGlobal({ globalGenerationInstructions: globalDraft });
      toast.success('Global instructions saved');
      setGlobalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save instructions');
    }
  };

  const isLoading = isInstructionsLoading || isDefinitionsLoading;

  return (
    <Container maxWidth={false} sx={{ my: 4, px: { xs: 2, md: 3 }, width: '100%', maxWidth: '100%' }}>
      <Card variant='outlined' sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.06)', p: 2 }}>
        <CardContent>
          <Typography variant='h5' fontWeight={700} mb={0.75}>
            Company Profile Setup
          </Typography>
          <Typography variant='body2' color='text.secondary' mb={3} sx={{ maxWidth: 720 }}>
            Define the card structure and AI instructions used to generate Company and Product
            profiles for all clients.
          </Typography>

          <Box display='flex' alignItems='center' justifyContent='space-between' gap={2} mb={2}>
            <Tabs
              value={scope}
              onChange={(_event, next) => {
                setScope(next);
                setFormOpen(false);
                setEditingId(null);
                setPendingDelete(null);
              }}
              sx={{ minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontWeight: 600 } }}
            >
              {SCOPES.map((item) => (
                <Tab key={item.id} value={item.id} label={item.label} />
              ))}
            </Tabs>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ textTransform: 'none' }}
            >
              Add card
            </Button>
          </Box>

          {isLoading ? (
            <Box display='flex' justifyContent='center' py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <GlobalInstructionsPanel onEdit={openGlobalEditor} />
              <SectionDefinitionGrid
                definitions={sortedDefinitions}
                selectedId={editingId}
                onEdit={openEdit}
                onDelete={setPendingDelete}
                onReorder={handleReorder}
              />
            </>
          )}
        </CardContent>
      </Card>

      <GlobalInstructionsModal
        open={globalOpen}
        value={globalDraft}
        onChange={setGlobalDraft}
        onClose={() => setGlobalOpen(false)}
        onSave={handleSaveGlobal}
        isSaving={isSavingGlobal}
      />

      <SectionDefinitionFormModal
        open={formOpen}
        mode={formMode}
        values={formValues}
        onChange={setFormValues}
        onClose={() => {
          setFormOpen(false);
          setEditingId(null);
        }}
        onSave={handleSaveCard}
        isSaving={isCreating || isUpdating}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => {
          if (!isDeleting) setPendingDelete(null);
        }}
        title='Delete card'
        content='Are you sure you want to delete this card?'
        cancelButton='No'
        confirmButton='Delete'
        confirmButtonProps={{ color: 'error', variant: 'contained' }}
        loading={isDeleting}
        onConfirm={handleDelete}
      />

    </Container>
  );
};

export default AdminCompanyProfileSetupPage;
